import { API_URL } from '../utils/config';

export const API_BASE_URL = API_URL;

/* Normal API requests */

const apiRequest = async (endpoint, method = 'GET', body = null) => {
    try {
        const token = localStorage.getItem('token');
        const headers = {
            'Content-Type': 'application/json',
        };

        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }

        const response = await fetch(`${API_BASE_URL}${endpoint}`, {
            method,
            headers,
            credentials: 'include',
            ...(body && { body: JSON.stringify(body) })
        });

        if (response.status === 401) {
            localStorage.removeItem('token');
            window.dispatchEvent(new CustomEvent('auth:expired'));
            const err = new Error('Session expired. Please log in again.');
            err.isAuthExpired = true;
            throw err;
        }

        if (!response.ok) {
            // Parse error response to get the message from backend
            const errorData = await response.json().catch(() => ({}));
            const errorMessage = errorData.message || errorData.error || `API request failed with status ${response.status}`;
            throw new Error(errorMessage);
        }

        if (response.status === 204) {
            return null;
        }

        const data = await response.json();
        return data;
    } catch (error) {
        console.error(`Error making ${method} request to ${endpoint}: `, error);
        throw error;
    }
};

/* File handlers */

export const compileDOCX = async (renderedText) => {
    return await compileDocument(`${API_BASE_URL}/utils/compile_docx`, renderedText);
};

export const compilePDF = async (renderedText) => {
    return await compileDocument(`${API_BASE_URL}/utils/compile_pdf`, renderedText);
};

const getAuthHeaders = () => ({
    'Authorization': `Bearer ${localStorage.getItem('token')}`,
    'Content-Type': 'application/json',
});

const compileDocument = async (endpoint, renderedText) => {
    const response = await fetch(endpoint, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({ renderedText: renderedText }),
    });

    if (response.status === 401) {
        localStorage.removeItem('token');
        window.dispatchEvent(new CustomEvent('auth:expired'));
        const err = new Error('Session expired. Please log in again.');
        err.isAuthExpired = true;
        throw err;
    }

    if (!response.ok) {
        throw new Error(`API request failed with status ${response.status}`);
    }

    const blob = await response.blob();
    return blob;
}

/* Non-standard API requests */

export const listenToSSE = (eventSourceUrl, onProgress, onError) => {
    const token = localStorage.getItem('token');
    const url = new URL(`${API_BASE_URL}${eventSourceUrl}`);
    
    // Add token as a query parameter since EventSource doesn't support custom headers
    if (token) {
        url.searchParams.append('auth_token', token.replace('Bearer ', ''));
    }
    
    const eventSource = new EventSource(url);

    eventSource.onmessage = (event) => {
        onProgress(event.data);
    };

    eventSource.onopen = () => {};

    eventSource.onerror = (error) => {
        console.error('SSE connection error:', error);
        eventSource.close();
        onError(error);
    };

    return eventSource;
};

export const uploadDocument = async (brainId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const token = localStorage.getItem('token');

    try {
        const response = await fetch(`${API_BASE_URL}/brains/${brainId}/upload_document`, {
            method: 'POST',
            headers: token ? { 'Authorization': `Bearer ${token}` } : {},
            credentials: 'include',
            body: formData,
        });

        if (response.status === 401) {
            localStorage.removeItem('token');
            window.dispatchEvent(new CustomEvent('auth:expired'));
            const err = new Error('Session expired. Please log in again.');
            err.isAuthExpired = true;
            throw err;
        }

        if (!response.ok) {
            throw new Error(`Failed to upload document: ${response.statusText}`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error uploading document:', error);
        throw error;
    }
}

export const getEmployers = async () => {    
    return await apiRequest('/employers', 'GET');  
}

export const getBrokers = async () => {    
    return await apiRequest('/brokers', 'GET');  
}

export const createEmployer = async ( name ) => {    
    return await apiRequest('/employers', 'POST', { name });  
}

export const createBroker = async ( name ) => {    
    return await apiRequest('/brokers', 'POST', { name });  
}

export const createTender = async (employerId, brokerId, comment) => {
    return await apiRequest('/tenders', 'POST', { employer_id: employerId, broker_id: brokerId, comment: comment });
}

export const cachedValidationResults = async (tenderId, step) => {
    return await apiRequest(`/tenders/${tenderId}/validation_results/${step}`, 'GET');
}

async function importDocuments(tenderId, files, endpoint) {
    const formData = new FormData();
    for (let i = 0; i < files.length; i++) {
        formData.append('documents[]', files[i]);
    }

    const token = localStorage.getItem("token");
    try {
        const response = await fetch(`${API_BASE_URL}/tenders/${tenderId}/${endpoint}`, {
            method: "POST",
            headers: token ? { "Authorization": `Bearer ${token}` } : {},
            credentials: "include",
            body: formData,
        });

        if (response.status === 401) {
            localStorage.removeItem('token');
            window.dispatchEvent(new CustomEvent('auth:expired'));
            const err = new Error('Session expired. Please log in again.');
            err.isAuthExpired = true;
            throw err;
        }

        if (!response.ok) {
            throw new Error(`Failed to upload file: ${response.statusText}`);
        }

        return response;
    } catch (error) {
        console.error("Error uploading files:", error);
        throw error;
    }
}

export const importSourceDocuments = async (tenderId, labeledFiles, isReimport = false) => {
    const formData = new FormData();
    
    for (const [insurerKey, file] of Object.entries(labeledFiles)) {
        if (insurerKey === 'hol' && file) {
            // Handle Hollard's dual file structure
            if (file.xml) {
                formData.append('hol_xml_document', file.xml);
            }
            if (file.docx) {
                formData.append('hol_docx_document', file.docx);
            }
        } else if (file) {
            formData.append(`${insurerKey}_document`, file);
        }
    }

    if (isReimport) {
        formData.append('is_reimport', 'true');
    }

    const token = localStorage.getItem("token");
    try {
        const response = await fetch(`${API_BASE_URL}/tenders/${tenderId}/import_source_documents`, {
            method: "POST",
            headers: token ? { "Authorization": `Bearer ${token}` } : {},
            credentials: "include",
            body: formData,
        });

        if (response.status === 401) {
            localStorage.removeItem('token');
            window.dispatchEvent(new CustomEvent('auth:expired'));
            const err = new Error('Session expired. Please log in again.');
            err.isAuthExpired = true;
            throw err;
        }

        if (!response.ok) {
            throw new Error(`Failed to upload file: ${response.statusText}`);
        }

        return response;
    } catch (error) {
        console.error("Error uploading files:", error);
        throw error;
    }
};
export const importFinalizedSummary = async (tenderId, files) => {
    return await importDocuments(tenderId, files, 'import_summary_sheet')
};
export const importPprSetFields = async (tenderId, files) => {
    return await importDocuments(tenderId, files, 'import_product_comparison')
};
export const importCurrentPolicy = async (tenderId, files) => {
    return await importDocuments(tenderId, files, 'import_client_comparison')
};

export const reselectSourceDocuments = async (tenderId) => {
    return await apiRequest(`/tenders/${tenderId}/restart_source_documents`, 'POST');
}
export const reselectFinalizedSummarySheet = async (tenderId) => {
    return await apiRequest(`/tenders/${tenderId}/restart_summary_sheet`, 'POST');
}
export const reselectPprSetFields = async (tenderId) => {
    return await apiRequest(`/tenders/${tenderId}/restart_product_comparison`, 'POST');
}
export const reselectClientPolicy = async (tenderId) => {
    return await apiRequest(`/tenders/${tenderId}/restart_client_comparison`, 'POST');
}

export const processSpreadsheet = async (tenderId) => {
    return await apiRequest(`/tenders/${tenderId}/process_source_documents`, 'POST');
}
export const processSummarySheetPdf = async (tenderId) => {
    return await apiRequest(`/tenders/${tenderId}/process_summary_sheet`, 'POST');
}
export const processProductComparison = async (tenderId) => {
    return await apiRequest(`/tenders/${tenderId}/process_product_comparison`, 'POST');
}
export const processClientComparison = async (tenderId, selectedInsurers) => {
    return await apiRequest(`/tenders/${tenderId}/process_client_comparison`, 'POST', 
        {selectedInsurers: selectedInsurers});
}

export const exportSpreadsheet = async (tenderId) => {
    return await downloadDocument(`/tenders/${tenderId}/export_summary_spreadsheet`);
}
export const exportSummarySheetPdf = async (tenderId) => {
    return await downloadDocument(`/tenders/${tenderId}/export_summary_sheet`);
}
export const exportProductComparison = async (tenderId) => {
    return await downloadDocument(`/tenders/${tenderId}/export_product_comparison`);
}
export const exportClientComparison = async (tenderId) => {
    return await downloadDocument(`/tenders/${tenderId}/export_client_comparison`);
}

export const fetchTenders = async (searchParams = {}) => {
    return await apiRequest(`/search_tenders`, 'POST', searchParams);
}

export const fetchDashboardData = async () => {
    return await apiRequest(`/dashboard_data`, 'GET');
}

export const fetchTender = async (tenderId) => {
    return await apiRequest(`/tenders/${tenderId}`, 'GET');
}

export const renameBroker = async (brokerId, newBrokerName) => {
    return await apiRequest(`/brokers/${brokerId}`, 'PATCH', { name: newBrokerName });
}

export const renameEmployer = async (employerId, newEmployerName) => {
    return await apiRequest(`/employers/${employerId}`, 'PATCH', { name: newEmployerName });
}

export const deleteTender = async (tenderId) => {
    return await apiRequest(`/tenders/${tenderId}`, 'DELETE');
}

export const resetTenderToFinalizedSummary = async (tenderId) => {
    return await apiRequest(`/tenders/${tenderId}/reset_to_finalized_summary`, 'POST');
}

export const resetTenderToProductComparison = async (tenderId) => {
    return await apiRequest(`/tenders/${tenderId}/reset_to_product_comparison`, 'POST');
}

export const reimportSourceDocuments = async (tenderId) => {
    return await apiRequest(`/tenders/${tenderId}/reimport_source_documents`, 'POST');
}

export const validateFinalizedSummary = async (tenderId) => {
    return await apiRequest(`/tenders/${tenderId}/validate_summary_sheet`, 'POST');
}

const downloadDocument = async (endpoint) => {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
    });

    if (response.status === 401) {
        localStorage.removeItem('token');
        window.dispatchEvent(new CustomEvent('auth:expired'));
        const err = new Error('Session expired. Please log in again.');
        err.isAuthExpired = true;
        throw err;
    }

    if (!response.ok) {
        throw new Error(`API request failed with status ${response.status}`);
    }

    const blob = await response.blob();
    const fileName = await response.headers.get('Content-Disposition').match(/filename="(.+)"/i)[1];
    
    performDownload(blob, fileName);
    
    return "OK";
}

const performDownload = (blob, fileName) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
};